import { Component, Inject, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import BriefingService from '@/service/briefing.service';
import vueCustomScrollbar from 'vue-custom-scrollbar';
import { isPaidUser } from '@/util/paid-user-util';

const appStoreState = namespace('appStore');
const briefingStore = namespace('briefingStore');

@Component({
  components: {
    'vue-custom-scrollbar': vueCustomScrollbar,
  },
})
export default class PopupUpdateBriefing extends Vue {
  @Inject('briefingService')
  private briefingService: () => BriefingService;

  @appStoreState.Mutation
  public setIsShowUpdateBriefing!: (isShowUpdateBriefing: boolean) => void;

  @briefingStore.Getter
  public getSelectedBriefingPage!: any;

  @briefingStore.Getter
  public getBriefingPages!: any[];

  @briefingStore.Mutation
  public setBriefingPages: (payload: any) => void;

  @briefingStore.Mutation
  public setSelectedBriefingPage: (payload: any) => void;

  @appStoreState.Getter
  public getIsShowSuccessUpdateBriefing!: boolean;

  @appStoreState.Mutation
  public setIsShowSuccessUpdateBriefing!: (payload: any) => void;

  @briefingStore.Getter
  public getSelectedUpdatingBriefingPage!: any;

  @appStoreState.Getter
  public getCurrentBriefingSummary!: string;

  @appStoreState.Mutation
  public setDisabledLink!: (payload: any) => void;

  @appStoreState.Getter
  public getChatSetting!: any;

  // Scrollbar settings
  scrollSettings = {
    suppressScrollY: false,
    suppressScrollX: true,
    wheelPropagation: false,
  };

  title = '';
  description = '';
  promptReportFocus = '';
  privacies = ['PUBLIC', 'PRIVATE'];
  privacy = 'PUBLIC';
  isLoading = false;

  reportTypes = [
    'DAILY',
    'PRODUCT_REVIEW',
    'INVESTMENT',
    'JOURNAL_NOTE',
    'DAILY_BLOG',
    'PRODUCT_REVIEW_BLOG',
    'INVESTMENT_BLOG',
    'JOURNAL_NOTE_BLOG',
  ];

  uiReportTypes = [
    { value: 'DAILY', label: 'General Report' },
    { value: 'INVESTMENT', label: 'Investment Report' },
    { value: 'PRODUCT_REVIEW', label: 'Comparison Report' },
    { value: 'INDEPTH_DAILY', label: 'In-depth report', isPaid: true },
  ];

  reportStyles = [
    { value: 'BUSINESS', label: this.$t('cdHome.t68') },
    { value: 'BLOG', label: this.$t('cdHome.t69') },
  ];

  selectedReportType = 'DAILY';
  selectedReportStyle = 'BUSINESS';
  reportTypesResult = ['DAILY'];

  bpId = this.getSelectedBriefingPage?.bpId || '';
  userKey = this.userIdentity?.id || JSON.parse(localStorage.getItem('localStorageUser'))?.id || '';
  tempPageName = this.$route.name || '';

  mounted() {
    this.setData();
  }

  get dataSources() {
    return this.getChatSetting?.dataSources || ['BASIC'];
  }

  isPaidUser(showPopup = true): boolean {
    return isPaidUser(showPopup);
  }

  get reportTypeForUI() {
    const currentType = this.reportTypesResult[0] || 'DAILY';
    return currentType.endsWith('_BLOG') ? currentType.replace('_BLOG', '') : currentType;
  }

  set reportTypeForUI(value) {
    const previousValue = this.reportTypeForUI;
    if (value === 'INDEPTH_DAILY' && !this.isPaidUser()) {
      const fallbackValue = previousValue !== 'INDEPTH_DAILY' ? previousValue : 'DAILY';

      setTimeout(() => {
        this.reportTypesResult[0] =
          this.selectedReportStyle === 'BLOG' && fallbackValue !== 'INDEPTH_DAILY' ? `${fallbackValue}_BLOG` : fallbackValue;
        this.selectedReportType = fallbackValue;

        this.$forceUpdate();
      }, 0);

      return;
    }

    if (this.selectedReportStyle === 'BLOG' && !value.endsWith('_BLOG') && value !== 'INDEPTH_DAILY') {
      this.reportTypesResult[0] = `${value}_BLOG`;
    } else {
      this.reportTypesResult[0] = value;
    }

    this.selectedReportType = value;
  }

  public get userIdentity(): any {
    return this.$store.getters?.account
      ? this.$store.getters?.account
      : this.$store.getters?.userIdentity
      ? this.$store.getters?.userIdentity
      : JSON.parse(localStorage.getItem('localStorageUser'))
      ? JSON.parse(localStorage.getItem('localStorageUser'))
      : null;
  }

  @Watch('getSelectedUpdatingBriefingPage')
  onGetSelectedUpdatingBriefingPageChange() {
    this.setData();
  }

  @Watch('getSelectedBriefingPage')
  onGetSelectedBriefingPageChange() {
    this.setData();
  }

  @Watch('$route')
  onRouteChange() {
    this.setIsShowUpdateBriefing(false);
  }

  @Watch('selectedReportType')
  @Watch('selectedReportStyle')
  onReportSelectionChange() {
    if (this.selectedReportType === 'INDEPTH_DAILY') {
      this.selectedReportStyle = 'BUSINESS';

      if (!this.isPaidUser()) {
        const previousValue = this.reportTypesResult[0].endsWith('_BLOG')
          ? this.reportTypesResult[0].replace('_BLOG', '')
          : this.reportTypesResult[0];

        this.selectedReportType = previousValue !== 'INDEPTH_DAILY' ? previousValue : 'DAILY';
        return;
      }
    }
    this.updateReportTypeResult();
  }

  @Watch('privacy')
  onPrivacyChange() {
    if (this.privacy === 'PRIVATE' && !this.isPaidUser(true)) {
      this.privacy = 'PUBLIC';
    }
  }

  @Watch('selectedReportStyle')
  onReportStyleChange() {
    if (this.selectedReportType === 'INDEPTH_DAILY') {
      this.selectedReportStyle = 'BUSINESS';
      return;
    }

    if (this.selectedReportStyle === 'BLOG' && this.selectedReportType !== 'INDEPTH_DAILY') {
      if (!this.reportTypesResult[0].endsWith('_BLOG')) {
        const baseType = this.reportTypesResult[0].replace('_BLOG', '');
        this.reportTypesResult[0] = `${baseType}_BLOG`;
      }
    } else {
      if (this.reportTypesResult[0].endsWith('_BLOG')) {
        this.reportTypesResult[0] = this.reportTypesResult[0].replace('_BLOG', '');
      }
    }
  }

  updateReportStyleFromType(reportType) {
    if (reportType.endsWith('_BLOG')) {
      this.selectedReportStyle = 'BLOG';
    } else if (reportType === 'INDEPTH_DAILY') {
      this.selectedReportStyle = 'BUSINESS';
    } else {
      this.selectedReportStyle = 'BUSINESS';
    }
  }

  setData() {
    let briefingData;

    if (this.getSelectedBriefingPage && !this.getSelectedUpdatingBriefingPage?.bpId) {
      briefingData = this.getSelectedBriefingPage;
    } else if (!this.getSelectedBriefingPage?.bpId && this.getSelectedUpdatingBriefingPage) {
      briefingData = this.getSelectedUpdatingBriefingPage;
    }

    if (this.reportTypesResult.length > 0) {
      const reportType = this.reportTypesResult[0];
      this.selectedReportType = reportType;
      this.updateReportStyleFromType(reportType);
    }

    if (briefingData) {
      this.title = briefingData.briefingPageName || '';
      this.description = briefingData.briefingDescription || this.getCurrentBriefingSummary || '';
      this.privacy = briefingData.privacy || 'PRIVATE';
      this.reportTypesResult = briefingData.reportTypes || ['DAILY'];
      this.promptReportFocus = briefingData.promptReportFocus || '';
      this.bpId = briefingData.bpId || '';

      if (this.reportTypesResult.length > 0) {
        const reportType = this.reportTypesResult[0];

        if (reportType.endsWith('_BLOG')) {
          this.selectedReportStyle = 'BLOG';
          this.selectedReportType = reportType.replace('_BLOG', '');
        } else {
          this.selectedReportStyle = 'BUSINESS';
          this.selectedReportType = reportType;
        }
      }
    }

    this.userKey = this.userIdentity?.id || JSON.parse(localStorage.getItem('localStorageUser'))?.id || '';
    this.tempPageName = this.$route.name || '';
  }

  updateReportTypeResult() {
    let reportType = this.selectedReportType;

    if (this.selectedReportStyle === 'BLOG' && !reportType.endsWith('_BLOG')) {
      if (reportType !== 'INDEPTH_DAILY') {
        reportType += '_BLOG';
      }
    }

    this.reportTypesResult = [reportType];
  }

  changeDataSource(data) {
    const sources = [...this.dataSources];

    if (!sources.includes(data)) {
      sources.push(data);
    } else {
      const index = sources.indexOf(data);
      if (index !== -1) {
        sources.splice(index, 1);
      }
    }

    if (sources.length === 0) {
      sources.push('BASIC');
    }

    const chatSettings = {
      ...this.getChatSetting,
      dataSources: sources,
    };
    this.$store.commit('appStore/setChatSetting', chatSettings);
    this.$forceUpdate;
  }

  handleCloseModalUpdateBriefingPage() {
    this.setIsShowUpdateBriefing(false);
  }

  async handleUpdateBriefing() {
    try {
      this.isLoading = true;
      const requestId = 'UB-' + new Date().getTime();
      const userKey = this.userIdentity?.id || JSON.parse(localStorage.getItem('localStorageUser'))?.id || '';
      const bpId = this.bpId;

      if (!this.title.trim() || !this.reportTypesResult.length) {
        this.isLoading = false;
        return;
      }

      const chatSetting = {
        ...this.getChatSetting,
        dataSources: this.dataSources,
      };

      this.$store.commit('appStore/setChatSetting', chatSetting);
      this.setSelectedBriefingPage({
        ...this.getSelectedBriefingPage,
        reportTypes: this.reportTypesResult,
        promptReportFocus: this.promptReportFocus,
        briefingPageName: this.title,
        briefingDescription: this.description,
        privacy: this.privacy,
        dataSources: this.dataSources,
      });

      const response = await this.briefingService().callAPIUpdateBriefingPage(
        requestId,
        userKey,
        bpId,
        this.title,
        this.description,
        this.privacy,
        this.reportTypesResult,
        chatSetting,
        this.promptReportFocus
      );

      if (response && response?.data?.body && response?.data?.body?.body?.bpId) {
        await this.refreshBriefingPages(userKey);

        this.setIsShowUpdateBriefing(false);
        this.setIsShowSuccessUpdateBriefing(true);
      }
    } catch (error) {
      console.error('Error updating briefing:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async refreshBriefingPages(userKey: string) {
    try {
      const response = await this.briefingService().getAllBriefingPage(userKey, false);
      if (response && response.data && response.data.briefingPages) {
        this.setBriefingPages(response.data.briefingPages);
      }
    } catch (error) {
      console.error('Error refreshing briefing pages:', error);
    }
  }

  handlePrivacyClick(event) {
    if (!this.isPaidUser(true)) {
      event.preventDefault();
      this.privacy = 'PUBLIC';
      return false;
    }
  }
}
